import { Protocol, config } from '../AuthConfig';
import { AuthChoice } from './data/AuthChoice';
import { Logger } from '../Logger';
import { AotCategory, logConstants } from '../../utils/Constants';

const FormsPrefix = 'forms-';

enum Connectivity {
	Direct = 'Direct',
	Gateway = 'Gateway',
	Any = 'Any',
}

interface ProtocolConfig {
	requiredConnectivity: Connectivity;
	enabled: boolean;
}

export class ProtocolSelector {
	private ProtocolConfigMap: Record<Protocol, ProtocolConfig> = {
		[Protocol.IntegratedWindows]: {
			requiredConnectivity: Connectivity.Direct,
			enabled: true,
		},
		[Protocol.CitrixAGBasic]: {
			requiredConnectivity: Connectivity.Gateway,
			enabled: true,
		},
		[Protocol.CitrixAGBasicNoPassword]: {
			requiredConnectivity: Connectivity.Gateway,
			enabled: true,
		},
		[Protocol.Certificate]: { requiredConnectivity: Connectivity.Direct, enabled: true },
		[Protocol.ExplicitForms]: { requiredConnectivity: Connectivity.Any, enabled: true },
		[Protocol.FormsVariant]: { requiredConnectivity: Connectivity.Any, enabled: true },
		[Protocol.HttpBasic]: {
			requiredConnectivity: Connectivity.Direct,
			enabled: config.httpBasicAuthEnabled ?? false,
		},
	};

	public getUsableAuthChoices(
		availableAuthChoices: AuthChoice[],
		usingGateway: boolean
	): AuthChoice[] {
		Logger.getInstance().write(
			logConstants.INFO,
			'Offered authentication protocols are: ' + JSON.stringify(availableAuthChoices),
			AotCategory.Info
		);
		Logger.getInstance().write(
			logConstants.INFO,
			'Configured protocol order is: ' + config.protocolOrder,
			AotCategory.Info
		);

		const requiredConnectivity = usingGateway
			? Connectivity.Gateway
			: Connectivity.Direct;
		const usableProtocolOrder = config.protocolOrder.filter(
			(protocol) =>
				this.ProtocolConfigMap[protocol]?.enabled &&
				(this.ProtocolConfigMap[protocol].requiredConnectivity === Connectivity.Any ||
					this.ProtocolConfigMap[protocol].requiredConnectivity === requiredConnectivity)
		);
		Logger.getInstance().write(
			logConstants.INFO,
			`Enabled protocols with ${requiredConnectivity} connectivity: ${usableProtocolOrder}`,
			AotCategory.Info
		);

		const parsedAvailableAuthChoices = availableAuthChoices.map((authChoice) => {
			return authChoice.getProtocol().toLowerCase().startsWith(FormsPrefix)
				? new AuthChoice(Protocol.FormsVariant, authChoice.getLocation())
				: authChoice;
		});

		const authChoices: AuthChoice[] = [];
		for (const protocol of usableProtocolOrder) {
			const matchedAuthChoice = parsedAvailableAuthChoices.find(
				(authChoice) => authChoice.getProtocol().toLowerCase() === protocol
			);
			if (matchedAuthChoice) {
				authChoices.push(matchedAuthChoice);
			}
		}

		Logger.getInstance().write(
			logConstants.INFO,
			'Usable protocols in order of precedence are: ' + JSON.stringify(authChoices),
			AotCategory.Info
		);
		return authChoices;
	}
}
